/**
 * Panel admin de magic-links de captura cliente (Sprint T2.2).
 *
 * Se monta en SiteAssessmentsPage. Permite al auditor:
 *   - Generar un enlace para que el cliente complete un formulario
 *     `_cliente` autoservicio.
 *   - Ver el estado de los enlaces emitidos (pending/submitted/
 *     promoted/expired/revoked).
 *   - Revocar enlaces pendientes.
 *   - Promover una submission a un Assessment real (con responses
 *     pre-cargadas).
 *
 * El token plano se muestra UNA SOLA VEZ tras crear, igual que en
 * portal cliente y recovery codes MFA.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { surveysApi, type SurveySummary } from '../api/surveys.js';
import {
  captureAdminApi,
  type CaptureTokenCreated,
  type CaptureTokenSummary,
} from '../api/capture.js';
import { ApiError } from '../api/client.js';

interface Props {
  siteId: string;
}

function statusBadge(status: CaptureTokenSummary['status']): { text: string; cls: string } {
  switch (status) {
    case 'pending':
      return { text: 'pendiente', cls: 'bg-yellow-100 text-yellow-800' };
    case 'submitted':
      return { text: 'recibido', cls: 'bg-blue-100 text-blue-800' };
    case 'promoted':
      return { text: 'promovido', cls: 'bg-green-100 text-green-800' };
    case 'expired':
      return { text: 'expirado', cls: 'bg-slate-100 text-slate-600' };
    case 'revoked':
      return { text: 'revocado', cls: 'bg-red-100 text-red-700' };
  }
}

export function CaptureTokensPanel({ siteId }: Props): JSX.Element {
  const navigate = useNavigate();
  const [tokens, setTokens] = useState<CaptureTokenSummary[]>([]);
  const [clienteTemplates, setClienteTemplates] = useState<SurveySummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [justCreated, setJustCreated] = useState<CaptureTokenCreated | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function refresh(): Promise<void> {
    try {
      // Usar GET /api/v1/surveys (requireAuth sin rol) en vez de GET /api/v1/admin/surveys
      // para que auditores (role:'auditor') puedan cargar las plantillas cliente sin 403.
      // El endpoint ya filtra solo publicadas, por eso no hace falta && t.is_published.
      const [tk, tpls] = await Promise.all([captureAdminApi.list(siteId), surveysApi.list()]);
      setTokens(tk.tokens);
      // Solo plantillas cliente y, por cada code, la ÚLTIMA versión publicada
      // (listPublished devuelve todas las versiones; ofrecer v1 y v2 confunde y
      // haría que un link nuevo use una versión sin reglas de scoring).
      const latestByCode = new Map<string, SurveySummary>();
      for (const t of tpls.templates) {
        if (!t.code.endsWith('_cliente')) continue;
        const cur = latestByCode.get(t.code);
        if (!cur || t.version > cur.version) latestByCode.set(t.code, t);
      }
      setClienteTemplates([...latestByCode.values()]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, [siteId]);

  async function handleCreate(): Promise<void> {
    if (!selectedTemplate) {
      setError('Selecciona una plantilla cliente');
      return;
    }
    setError(null);
    setCreating(true);
    try {
      const input: { template_id: string; client_email?: string } = {
        template_id: selectedTemplate,
      };
      if (clientEmail.trim()) input.client_email = clientEmail.trim();
      const created = await captureAdminApi.create(siteId, input);
      setJustCreated(created);
      setClientEmail('');
      await refresh();
    } catch (err) {
      if (err instanceof ApiError) setError(err.code);
      else setError(err instanceof Error ? err.message : 'Error');
    } finally {
      setCreating(false);
    }
  }

  async function handleRevoke(tokenId: string): Promise<void> {
    if (!confirm('¿Revocar este enlace? El cliente no podra usarlo mas.')) return;
    try {
      await captureAdminApi.revoke(siteId, tokenId);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error');
    }
  }

  async function handlePromote(tokenId: string): Promise<void> {
    if (
      !confirm(
        'Convertir esta submission en un assessment? El assessment quedara con las respuestas precargadas, status completed, asignado a tu usuario.',
      )
    )
      return;
    try {
      const r = await captureAdminApi.promote(siteId, tokenId);
      // ADR-040 D4/D5: si el backend encontró plantilla gemela auditor y aplicó
      // el prefill, navegar directamente a continuar la instancia AUDITOR
      // (in_progress, pre-llenada). La instancia cliente (completed, evidencia)
      // se verá como tab "Cliente (evidencia)" en AssessmentCapture.
      // Si no hay gemelo (skipped_no_twin), comportamiento anterior: abre el
      // assessment en capture sin instancia auditor diferenciada.
      if (r.auditor_prefill === 'applied' && r.auditor_instance_id) {
        navigate(`/assessments/${r.assessment_id}/capture`, {
          state: { activeInstanceId: r.auditor_instance_id },
        });
      } else {
        navigate(`/assessments/${r.assessment_id}/capture`);
      }
    } catch (err) {
      if (err instanceof ApiError) setError(err.code);
      else setError(err instanceof Error ? err.message : 'Error');
    }
  }

  async function copyToClipboard(text: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Algunos browsers requieren HTTPS o user gesture; fallback silencioso.
    }
  }

  function publicUrlOf(captureUrl: string): string {
    // capture_url viene relativo (/capture/<token>); le concatenamos
    // origin para que el copy/paste sea util.
    if (typeof window === 'undefined') return captureUrl;
    return new URL(captureUrl, window.location.origin).toString();
  }

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5 space-y-4">
      <header className="flex items-center justify-between">
        <h2 className="font-semibold text-slate-900">Captura cliente — formularios previos</h2>
        <span className="text-xs text-slate-500">
          {tokens.filter((t) => t.status === 'pending').length} pendientes
        </span>
      </header>

      {justCreated && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 space-y-2">
          <p className="text-sm font-semibold text-amber-900">Enlace generado</p>
          <p className="text-xs text-amber-800">
            Copia y comparte la URL con el cliente. No volveras a ver el token completo despues de
            cerrar este mensaje.
          </p>
          <div className="rounded bg-white border border-amber-300 p-2 font-mono text-[10px] break-all text-amber-900">
            {publicUrlOf(justCreated.capture_url)}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => {
                void copyToClipboard(publicUrlOf(justCreated.capture_url));
              }}
              className="rounded-md bg-amber-700 text-white px-3 py-1.5 text-xs font-medium hover:bg-amber-700/90"
            >
              Copiar URL
            </button>
            <button
              type="button"
              onClick={() => setJustCreated(null)}
              className="rounded-md border border-amber-300 px-3 py-1.5 text-xs font-medium text-amber-900 hover:bg-amber-100"
            >
              Cerrar
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 items-end">
        <div className="space-y-1">
          <label htmlFor="capture-template" className="block text-xs font-medium text-slate-700">
            Plantilla cliente
          </label>
          <select
            id="capture-template"
            value={selectedTemplate}
            onChange={(e) => setSelectedTemplate(e.target.value)}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
          >
            <option value="">— selecciona —</option>
            {clienteTemplates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} (v{t.version})
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <label htmlFor="capture-email" className="block text-xs font-medium text-slate-700">
            Email cliente (opcional, audit trail)
          </label>
          <div className="flex gap-2">
            <input
              id="capture-email"
              type="email"
              placeholder="cliente@empresa.com"
              value={clientEmail}
              onChange={(e) => setClientEmail(e.target.value)}
              className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
            />
            <button
              type="button"
              disabled={creating || !selectedTemplate}
              onClick={() => {
                void handleCreate();
              }}
              className="rounded-lg bg-brand-700 px-4 py-2 text-sm font-medium text-white disabled:bg-slate-300"
            >
              {creating ? 'Creando...' : 'Generar enlace'}
            </button>
          </div>
        </div>
      </div>
      <p className="text-[10px] text-slate-500">
        El enlace caduca a los 30 dias. Solo permite un envio (write-once). Una vez recibido, podras
        promover la submission a un assessment.
      </p>

      {error && (
        <p role="alert" className="text-sm text-red-700">
          {error}
        </p>
      )}

      {loading ? (
        <p className="text-xs text-slate-400">Cargando enlaces...</p>
      ) : tokens.length === 0 ? (
        <p className="text-xs text-slate-400">Aun no se genero ningun enlace de captura.</p>
      ) : (
        <ul className="divide-y divide-slate-200 border border-slate-200 rounded-lg">
          {tokens.map((t) => {
            const badge = statusBadge(t.status);
            return (
              <li key={t.id} className="px-3 py-2 text-xs">
                <div className="flex justify-between items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-mono text-slate-700">{t.id.slice(0, 8)}</div>
                    <div className="text-[10px] text-slate-500">
                      creado {new Date(t.created_at).toLocaleDateString('es-DO')} · expira{' '}
                      {new Date(t.expires_at).toLocaleDateString('es-DO')}
                      {t.client_email && ` · ${t.client_email}`}
                      {t.submitted_at &&
                        ` · recibido ${new Date(t.submitted_at).toLocaleString('es-DO')}`}
                    </div>
                  </div>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${badge.cls}`}>
                    {badge.text}
                  </span>
                  <div className="flex flex-col gap-1">
                    {t.status === 'submitted' && (
                      <button
                        type="button"
                        onClick={() => {
                          void handlePromote(t.id);
                        }}
                        className="rounded-md bg-green-700 text-white px-2 py-1 text-[10px] font-medium hover:bg-green-700/90"
                      >
                        Promover
                      </button>
                    )}
                    {t.status === 'promoted' && t.promoted_assessment_id && (
                      <button
                        type="button"
                        onClick={() =>
                          navigate(`/assessments/${t.promoted_assessment_id!}/capture`)
                        }
                        className="rounded-md border border-green-300 px-2 py-1 text-[10px] font-medium text-green-700 hover:bg-green-50"
                      >
                        Ver assessment
                      </button>
                    )}
                    {t.status === 'pending' && (
                      <button
                        type="button"
                        onClick={() => {
                          void handleRevoke(t.id);
                        }}
                        className="rounded-md border border-red-300 px-2 py-1 text-[10px] font-medium text-red-700 hover:bg-red-50"
                      >
                        Revocar
                      </button>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
